import { jsPDF } from 'jspdf';
import { calculatePageDimensions } from './pdfService/dimensions';
import { getPageElements } from './pdfService/pageElements';
import { renderPageToPDF } from './pdfService/pageRenderer';
import { setupPDFFonts } from '../utils/pdfFonts';
import { generateCoverPDF as generateCoverPDFService } from './pdfService/coverPDF';
import '../assets/fonts/Baskervville-Regular-normal.js'; // Import Baskervville font

// Convert ArrayBuffer to Base64
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export async function generateVerticalPDF(book, options = {}) {
  try {
    console.log('🔍 Starting vertical PDF generation with options:', options);
    console.log('📚 Book data structure:', {
      hasSpread: !!book?.spreads,
      totalPages: book?.totalPages,
      pageSize: book?.pageSize,
      spreads: Object.keys(book?.spreads || {})
    });
    
    // Get the book's page size or use default
    const pageSize = book.pageSize || 'us-trade';
    
    // For Lulu printing, we may need special handling for certain sizes
    const isForLuluPrinting = options.forLuluPrinting || false;
    
    let width, height, scale;
    if (isForLuluPrinting) {
      // When printing through Lulu, we apply special settings to ensure PDF compliance
      console.log(`📏 Generating Lulu-compatible print-ready PDF with ${pageSize} size`);
      console.log('✅ Applied Lulu PDF compatibility settings: embedded fonts, flattened transparency');
      
      // Save these settings so components know to apply special handling
      options.luluCompatibility = {
        flattenTransparency: true,
        embedFonts: true,
        useSRGB: true,
        qualityLevel: 'print'
      };
    }
    
    // Calculate dimensions based on the selected page size
    const dims = calculatePageDimensions(pageSize);
    width = dims.width;
    height = dims.height;
    scale = dims.scale;
    
    console.log(`📄 PDF dimensions set to: ${width}pt x ${height}pt (${pageSize})`);
    
    // Initialize PDF with proper settings for print compatibility
    const doc = new jsPDF({
      unit: 'pt',
      format: [width, height],
      compress: true,
      putOnlyUsedFonts: true
    });
    
    // Set PDF metadata and properties for better print compatibility
    doc.setProperties({
      title: book.title || 'Storybook',
      author: 'StorybookWiz',
      subject: 'Children\'s Book',
      keywords: 'storybook, children, book'
    });

    doc.setFont('times', 'normal'); // Set font after initialization

    // Register custom fonts
    await setupPDFFonts(doc);
    console.log('✓ Fonts setup complete');

    // Get cover color
    const coverColor =
      book?.coverDetails?.frontCover?.color ||
      book?.frontCoverColor ||
      '#FFFFFF';

    // Skip cover option
    const skipCover = options.skipCover || false;
    console.log(`🖼️ Cover pages will be ${skipCover ? 'SKIPPED' : 'included'}`);

    // Add debugging info to the first page if in debug mode
    if (options.debug) {
      doc.setFontSize(10);
      doc.text('DEBUG MODE - Vertical PDF', 20, 20);
      doc.text(`Generated: ${new Date().toISOString()}`, 20, 35);
      doc.text(`Total Pages: ${book.totalPages}`, 20, 50);
      doc.text(`Skip Cover: ${skipCover}`, 20, 65);
    }

    // Start with the first content page
    let isFirstPage = skipCover ? true : false;

    // Front cover - only if not skipping cover
    if (!skipCover) {
      const frontCoverElements = getPageElements(book, 0);
      console.log(`📄 Front Cover: Found ${frontCoverElements.length} elements`);
      await renderPageToPDF(doc, frontCoverElements, {
        scale,
        isCover: true,
        coverColor,
        debug: options.debug
      });
      isFirstPage = false;
    }

    // Add a title page
    if (!isFirstPage) {
      doc.addPage();
    }

    // Helper functions for color handling
    function hexToRgb(hex) {
      // Remove the hash if it exists
      hex = hex.replace(/^#/, '');
      
      // Parse the hex value
      const r = parseInt(hex.substring(0, 2), 16) || 0;
      const g = parseInt(hex.substring(2, 4), 16) || 0;
      const b = parseInt(hex.substring(4, 6), 16) || 0;
      
      return { r, g, b };
    }
    
    function rgbToHex(r, g, b) {
      return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    }

    // Set up the title page
    console.log('📄 Adding title page with book title');

    // Use plain white background for the title page
    doc.setFillColor('#FFFFFF');
    doc.rect(
      0,
      0,
      width,
      height,
      'F'
    );
    
    // Get the book title and author or use defaults
    const bookTitle = book.title || 'Untitled Book';
    const bookAuthor = book.author || '';
    
    // Set font for the title
    doc.setFont('Baskervville-Regular', 'normal');
    doc.setFontSize(42);
    doc.setTextColor(0, 0, 0);
    
    // Handle long titles - calculate appropriate font size and wrap if needed
    let titleFontSize = 42; // Default font size
    const maxWidth = width * 0.8; // Maximum width for title (80% of page width)
    
    // Calculate title width to check if we need to adjust font size
    doc.setFontSize(titleFontSize);
    const titleWidth = doc.getTextWidth(bookTitle);
    
    // If title is too wide, reduce font size or add wrapping
    if (titleWidth > maxWidth) {
      // First approach: reduce font size if it's just slightly too wide
      if (titleWidth < maxWidth * 1.5) {
        // Adjust font size to fit
        titleFontSize = Math.max(24, titleFontSize * (maxWidth / titleWidth));
        doc.setFontSize(titleFontSize);
      } else {
        // For very long titles, keep reasonable font size but wrap text
        titleFontSize = 32; // Slightly smaller but still prominent
        doc.setFontSize(titleFontSize);
      }
    }
    
    // Render the title centered on the page with appropriate wrapping
    doc.text(
      bookTitle,
      width / 2,
      height / 4,  // Position at 1/4 from the top
      { 
        align: 'center',
        maxWidth: maxWidth
      }
    );
    
    // Get the height of the title text (including wrapping if any)
    const titleDims = typeof doc.getTextDimensions === 'function'
      ? doc.getTextDimensions(bookTitle, { maxWidth })
      : { h: titleFontSize * 1.2 };
    const titleTextHeight = titleDims.h;
    
    // Add author if available
    if (bookAuthor) {
      doc.setFontSize(24);
      doc.setTextColor(60, 60, 60);
      doc.text(
        'By ' + bookAuthor,
        width / 2,
        height / 4 + titleTextHeight + 40, // 40pts below the title
        { align: 'center' }
      );
    }
    
    // Add attribution on two lines at the bottom of the page
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128); // Medium gray color
    // Use a single text element with newline character
    doc.text(
      'Created With\nStorybook Wizard',
      width / 2,
      height - 80, // Slightly higher from the bottom
      { 
        align: 'center',
        lineHeightFactor: 1.5 // Adjust line spacing
      }
    );
    
    // Add debug info if in debug mode
    if (options.debug) {
      doc.setFontSize(8);
      doc.setTextColor(255, 0, 0);
      doc.text('Title Page', 5, height - 5);
    }
    
    // Mark that we've rendered the title page
    isFirstPage = false;

    // Content pages
    const totalPages = parseInt(book.totalPages, 10);
    const pageStart = Math.max(1, parseInt(options.pageStart || 1, 10));
    const pageEnd = Math.min(totalPages, parseInt(options.pageEnd || totalPages, 10));
    const totalToRender = pageEnd - pageStart + 1;
    console.log(`📚 Rendering ${totalPages} content pages`);
    
    // IMPORTANT: Add a debug overlay to each page to verify they're being generated
    let omitImagesFallback = false; // auto-switch after first timeout
    for (let pageNumber = pageStart; pageNumber <= pageEnd; pageNumber++) {
      // Yield to the event loop to keep the UI responsive
      // This prevents the browser "page is slowing down" warning during long generations
      // and allows rendering/inputs to process between pages.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 0));
      // Add a new page if this isn't the first page rendered
      if (!isFirstPage) {
        doc.addPage();
      }
      isFirstPage = false;
      
      // Get the elements for this page
      const elements = getPageElements(book, pageNumber);
      
      // Log the elements to debug console
      console.log(`📄 Page ${pageNumber}: Found ${elements.length} elements`);
      
      // Render the elements to the PDF with a safety timeout (configurable)
      let timedOut = false;
      let pageErrored = false;
      const perPageTimeoutMs = Number.isFinite(options.pageRenderTimeoutMs) ? options.pageRenderTimeoutMs : 3000;
      try {
        await Promise.race([
          renderPageToPDF(doc, elements, { 
            scale,
            pageNumber,
            debug: options.debug,
            hasPageNumbers: book.hasPageNumbers,
            omitImages: omitImagesFallback || options.omitImages,
            width,
            height
          }),
          new Promise((resolve) => setTimeout(() => { timedOut = true; resolve('timeout'); }, perPageTimeoutMs))
        ]);
      } catch (pageErr) {
        console.warn(`⚠️ Page ${pageNumber} render error. Re-rendering WITHOUT images and continuing.`, pageErr);
        pageErrored = true;
      }

      // If timed out or errored, re-render this page without images (fast) so the rest can keep full images
      if (timedOut || pageErrored) {
        console.warn(`⏱️ Page ${pageNumber} render timed out or errored. Re-rendering WITHOUT images and continuing.`);
        try {
          // eslint-disable-next-line no-await-in-loop
          await renderPageToPDF(doc, elements, {
            scale,
            pageNumber,
            debug: options.debug,
            hasPageNumbers: book.hasPageNumbers,
            omitImages: true,
            width,
            height
          });
        } catch (rerenderErr) {
          console.error('Failed to re-render page without images, drawing placeholder:', rerenderErr);
          doc.setDrawColor(255, 0, 0);
          doc.setLineWidth(1);
          doc.rect(10, 10, width - 20, height - 20, 'S');
          doc.setFontSize(12);
          doc.setTextColor(255, 0, 0);
          doc.text(`Page ${pageNumber} rendering timed out or errored`, width / 2, height / 2, { align: 'center' });
        }
      }
      
      // Add page number debugging
      if (options.debug) {
        doc.setFontSize(8);
        doc.setTextColor(255, 0, 0);
        doc.text(`Page ${pageNumber}`, 5, height - 5);
      }

      // Progress callback
      try {
        if (typeof options.onProgress === 'function') {
          const current = pageNumber - pageStart + 1;
          options.onProgress({ type: 'vertical', current, total: totalToRender, pageNumber });
        }
      } catch (_) {}
    }

    // Back cover - only if not skipping cover
    if (!skipCover) {
      doc.addPage();
      const backCoverElements = getPageElements(book, totalPages + 1);
      console.log(`📄 Back Cover: Found ${backCoverElements.length} elements`);
      await renderPageToPDF(doc, backCoverElements, {
        scale,
        isCover: true,
        coverColor,
        debug: options.debug
      });
    }

    console.log('✓ Vertical PDF generation complete');
    return doc.output('blob');
  } catch (error) {
    console.error('❌ Error generating vertical PDF:', error);
    throw error;
  }
}

export async function generateCoverPDF(book, options = {}) {
  try {
    console.log('🔄 Forwarding to coverPDF.js with options:', options);
    // Pass all options to the coverPDF generator
    return await generateCoverPDFService(book, options);
  } catch (error) {
    console.error('Error generating cover PDF:', error);
    throw error;
  }
}

export { generateHorizontalPDF } from './pdfService/horizontalPDF';
